import React from "react";
import i18next from "i18next";
import {Button} from "antd";
import {ArrowLeftOutlined, CheckCircleFilled} from "@ant-design/icons";
import * as Setting from "../../../Setting";

function getSteps() {
  return [
    {title: i18next.t("forget:Account"), subtitle: i18next.t("forget:Enter the email or username on your Aviron account")},
    {title: i18next.t("forget:Verify"), subtitle: i18next.t("forget:We will send you a code to confirm it is you")},
    {title: i18next.t("forget:Reset"), subtitle: i18next.t("forget:Choose a new password")},
  ];
}

// Shown once the new password is saved, instead of redirecting to sign-in right away
function renderResetDone(page) {
  return (
    <div className="aviron-reset-done" role="status">
      <CheckCircleFilled className="aviron-reset-done-icon" aria-hidden="true" />
      <h1 className="aviron-title">{i18next.t("forget:Password updated")}</h1>
      <p className="aviron-subtitle">{i18next.t("forget:You can now sign in with your new password")}</p>
      <Button type="primary" block className="login-button" onClick={() => page.goToSignin()}>
        {i18next.t("login:Sign In")}
      </Button>
    </div>
  );
}

// The login card the forgot-password page lives in, with the application's custom CSS applied as
// on the original page
function renderCard(application, children) {
  return (
    <div className="login-content aviron-forget">
      {Setting.inIframe() || Setting.isMobile() ? null : <div dangerouslySetInnerHTML={{__html: application.formCss}} />}
      {Setting.inIframe() || !Setting.isMobile() ? null : <div dangerouslySetInnerHTML={{__html: application.formCssMobile}} />}
      <div className="login-panel">
        <div className="login-form">
          {Setting.renderHelmet(application)}
          {children}
        </div>
      </div>
    </div>
  );
}

// The Aviron shell of the forgot-password page: a back button, the logo, the title, a three-step
// progress bar and ForgetPage's own forms. `page` is the ThemedForgetPage instance, which keeps
// ForgetPage's state and logic.
export function renderAvironForgetPage(page, application) {
  const logo = (
    <div className="login-logo-box">
      {Setting.renderLogo(application)}
    </div>
  );

  if (page.state.resetDone) {
    return renderCard(application, <React.Fragment>{logo}{renderResetDone(page)}</React.Fragment>);
  }

  const steps = getSteps();
  const current = page.state.current;
  // the sign-in page this flow started from (e.g. an OAuth authorize URL with all its parameters),
  // stored by its "Forgot password?" link; the application's login link otherwise
  const loginLink = sessionStorage.getItem("signinUrl") || Setting.getLoginLink(application);

  return renderCard(application, (
    <React.Fragment>
      <Button type="text" className="aviron-back-button" icon={<ArrowLeftOutlined />} onClick={() => page.stepBack()}>
        {i18next.t("general:Back")}
      </Button>
      {logo}
      <div className="aviron-heading">
        <h1 className="aviron-title">{i18next.t("forget:Reset password")}</h1>
        <p className="aviron-subtitle">{steps[current]?.subtitle}</p>
      </div>
      <ol className="aviron-steps">
        {
          steps.map((step, index) => (
            <li key={step.title} className={index < current ? "is-done" : index === current ? "is-current" : null} aria-current={index === current ? "step" : undefined}>
              <span className="aviron-steps-bar" aria-hidden="true" />
              <span className="aviron-steps-label">{step.title}</span>
            </li>
          ))
        }
      </ol>
      {page.renderForm(application)}
      {
        // ForgetPage shows the password rules in a popover beside the field, which covers the
        // field on narrower screens; here the same, live checklist sits under the form instead
        current === 2 && page.state.passwordPopoverOpen ? (
          <div className="aviron-password-rules" aria-live="polite">
            {page.state.passwordPopover}
          </div>
        ) : null
      }
      {
        loginLink === null ? null : (
          <p className="aviron-forget-footer">
            {i18next.t("forget:Remember your password?")}&nbsp;
            <a href={loginLink} onClick={(e) => {
              e.preventDefault();
              page.goToSignin();
            }}>
              {i18next.t("forget:Back to sign in")}
            </a>
          </p>
        )
      }
    </React.Fragment>
  ));
}
